import "@/App.css";
import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Layout } from "./layouts/Layout";
import { LoginPage } from "./pages/LoginPage";
import { getCurrentUser } from "./services/api";
import { DataProvider } from "./services/DataContext";
import { OfflineIndicator, PWAInstallBanner } from "./components/PWAComponents";
import { initializePWA, setupOnlineOfflineHandlers } from "./services/pwaService";
import { toast } from "sonner";

// Lazy load pages for faster navigation
const DashboardPage = lazy(() => import("./pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const TasksPage = lazy(() => import("./pages/TasksPage").then(m => ({ default: m.TasksPage })));
const LabPage = lazy(() => import("./pages/LabPage").then(m => ({ default: m.LabPage })));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage").then(m => ({ default: m.ReviewsPage })));
const ReferralsPage = lazy(() => import("./pages/ReferralsPage").then(m => ({ default: m.ReferralsPage })));
const PedidosPage = lazy(() => import("./pages/PedidosPage").then(m => ({ default: m.PedidosPage })));
const SurgicalLogbookPage = lazy(() => import("./pages/SurgicalLogbookPage").then(m => ({ default: m.SurgicalLogbookPage })));
const AuxiliaresPage = lazy(() => import("./pages/AuxiliaresPage").then(m => ({ default: m.AuxiliaresPage })));
const VacacionesPage = lazy(() => import("./pages/VacacionesPage").then(m => ({ default: m.VacacionesPage })));
const MmPage = lazy(() => import("./pages/MmPage").then(m => ({ default: m.MmPage })));
const UrgenciasPage = lazy(() => import("./pages/UrgenciasPage").then(m => ({ default: m.UrgenciasPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage })));

// Keep other imports for full version (hidden but available)
const HospitalizedPage = lazy(() => import("./pages/HospitalizedPage").then(m => ({ default: m.HospitalizedPage })));
const PatientDetailPage = lazy(() => import("./pages/PatientDetailPage").then(m => ({ default: m.PatientDetailPage })));
const NewPatientPage = lazy(() => import("./pages/NewPatientPage").then(m => ({ default: m.NewPatientPage })));
const HistoryPage = lazy(() => import("./pages/HistoryPage").then(m => ({ default: m.HistoryPage })));
const AnesthesiaPage = lazy(() => import("./pages/AnesthesiaPage").then(m => ({ default: m.AnesthesiaPage })));
const AnesthesiaTACPage = lazy(() => import("./pages/AnesthesiaTACPage"));
const AIReportPage = lazy(() => import("./pages/AIReportPage").then(m => ({ default: m.AIReportPage })));
const RevisionReportPage = lazy(() => import("./pages/RevisionReportPage").then(m => ({ default: m.RevisionReportPage })));
const ShiftsPage = lazy(() => import("./pages/ShiftsPage").then(m => ({ default: m.ShiftsPage })));
const OrdersPage = lazy(() => import("./pages/OrdersPage").then(m => ({ default: m.OrdersPage })));
const InvoicesPage = lazy(() => import("./pages/InvoicesPage").then(m => ({ default: m.InvoicesPage })));
const AuthorizationsPage = lazy(() => import("./pages/AuthorizationsPage").then(m => ({ default: m.AuthorizationsPage })));
const InformedConsentPage = lazy(() => import("./pages/InformedConsentPage").then(m => ({ default: m.InformedConsentPage })));
const InterpreterPage = lazy(() => import("./pages/InterpreterPage").then(m => ({ default: m.InterpreterPage })));
const CommissionsPage = lazy(() => import("./pages/CommissionsPage").then(m => ({ default: m.CommissionsPage })));
const VacationsPage = lazy(() => import("./pages/VacationsPage").then(m => ({ default: m.VacationsPage })));
const SuggestionsPage = lazy(() => import("./pages/SuggestionsPage").then(m => ({ default: m.SuggestionsPage })));
const ReportsPage = lazy(() => import("./pages/ReportsPage").then(m => ({ default: m.ReportsPage })));
const InformesPage = lazy(() => import("./pages/InformesPage").then(m => ({ default: m.InformesPage })));
const FichajesPage = lazy(() => import("./pages/FichajesPage").then(m => ({ default: m.FichajesPage })));
const DischargeInstructionsPage = lazy(() => import("./pages/DischargeInstructionsPage").then(m => ({ default: m.DischargeInstructionsPage })));
const GenerateReportPage = lazy(() => import("./pages/treatment").then(m => ({ default: m.GenerateReportPage })));
const MedicationsPage = lazy(() => import("./pages/treatment").then(m => ({ default: m.MedicationsPage })));
const PresetsPage = lazy(() => import("./pages/treatment").then(m => ({ default: m.PresetsPage })));
const RegisterPatientPage = lazy(() => import("./pages/patients").then(m => ({ default: m.RegisterPatientPage })));
const PatientsDatabasePage = lazy(() => import("./pages/patients").then(m => ({ default: m.PatientsDatabasePage })));

// Simple loading spinner for page transitions
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
  </div>
);

// Prefetch heavy chunks after initial load
const prefetchRoutes = () => {
  setTimeout(() => {
    import("./pages/PedidosPage").catch(() => {});
    import("./pages/UrgenciasPage").catch(() => {});
    import("./pages/FichajesPage").catch(() => {});
  }, 2000);
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    const token = localStorage.getItem('benivet_token');
    if (token) {
      try {
        const response = await getCurrentUser();
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('benivet_token');
        localStorage.removeItem('benivet_user');
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAuth();
    // Initialize PWA
    initializePWA().then((status) => {
      console.log('[PWA] Initialized:', status);
    });
    // Setup online/offline handlers
    setupOnlineOfflineHandlers(
      (result) => {
        if (result.synced > 0) {
          toast.success(`Reconectado: ${result.synced} cambios sincronizados`);
        }
      },
      () => {
        toast.warning('Sin conexión - Modo offline activado');
      }
    );
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    prefetchRoutes();
  };

  const handleLogout = () => {
    localStorage.removeItem('benivet_token');
    localStorage.removeItem('benivet_user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#E6F7F4] via-white to-[#F5F7FA]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AE95]"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        {!user ? (
          <Routes>
            <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        ) : (
          <DataProvider>
            <Layout user={user} onLogout={handleLogout}>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/tasks" element={<TasksPage user={user} />} />
                  <Route path="/hospitalized" element={<HospitalizedPage />} />
                  <Route path="/hospitalized/:id" element={<PatientDetailPage />} />
                  <Route path="/new-patient" element={<RegisterPatientPage />} />
                  <Route path="/patients" element={<PatientsDatabasePage />} />
                  <Route path="/patients/register" element={<RegisterPatientPage />} />
                  <Route path="/patients/database" element={<PatientsDatabasePage />} />
                  <Route path="/history" element={<HistoryPage />} />
                  <Route path="/anesthesia" element={<AnesthesiaPage />} />
                  <Route path="/anesthesia-tac" element={<AnesthesiaTACPage />} />
                  <Route path="/orders" element={<OrdersPage />} />
                  <Route path="/lab" element={<LabPage />} />
                  <Route path="/shifts" element={<ShiftsPage />} />
                  <Route path="/ai-report" element={<AIReportPage />} />
                  <Route path="/revision-report" element={<RevisionReportPage />} />
                  <Route path="/discharge-instructions" element={<GenerateReportPage />} />
                  <Route path="/treatment" element={<GenerateReportPage />} />
                  <Route path="/treatment/generate" element={<GenerateReportPage />} />
                  <Route path="/treatment/medications" element={<MedicationsPage />} />
                  <Route path="/treatment/presets" element={<PresetsPage />} />
                  <Route path="/reviews" element={<ReviewsPage />} />
                  <Route path="/referrals" element={<ReferralsPage />} />
                  <Route path="/compras" element={<PedidosPage />} />
                  <Route path="/surgical-logbook" element={<SurgicalLogbookPage />} />
                  <Route path="/auxiliares" element={<AuxiliaresPage />} />
                  <Route path="/vacaciones" element={<VacacionesPage />} />
                  <Route path="/urgencias" element={<UrgenciasPage />} />
                  <Route path="/mm" element={<MmPage />} />
                  {/* catalogo moved inside compras */}
                  <Route path="/invoices" element={<InvoicesPage />} />
                  <Route path="/authorizations" element={<AuthorizationsPage />} />
                  <Route path="/informed-consent" element={<InformedConsentPage />} />
                  <Route path="/interpreter" element={<InterpreterPage />} />
                  <Route path="/commissions" element={<CommissionsPage />} />
                  <Route path="/vacations" element={<VacationsPage />} />
                  <Route path="/suggestions" element={<SuggestionsPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/informes" element={<InformesPage />} />
                  <Route path="/fichajes" element={<FichajesPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/login" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </Layout>
          </DataProvider>
        )}
      </BrowserRouter>
      <Toaster 
        position="top-right" 
        richColors 
        closeButton
        toastOptions={{
          style: {
            fontFamily: 'Inter, sans-serif',
          },
        }}
      />
      {/* PWA Components */}
      <OfflineIndicator />
      <PWAInstallBanner />
    </div>
  );
}

export default App;
